import { Button, Progress } from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { AddFaqDialog, type FaqDialogMode } from './AddFaqDialog';
import {
  DefaultFaqsPanel,
  type DefaultFaqsPanelItem,
} from '@/shared/default-faqs/DefaultFaqsPanel';
import {
  DEFAULT_FAQS_CARD_TITLE,
  defaultFaqsCardCopy,
} from '@/shared/default-faqs/default-faqs-card-copy';
import { ArchiveIcon, PlusIcon, SquarePenIcon, Trash2Icon, TriangleAlertIcon } from './icons';
import { RichTextContent } from '@/shared/rich-text-content/RichTextContent';

export interface ProgramFaq {
  id: string;
  question: string;
  answer: string;
}

export interface ProgramFaqsPageProps {
  faqs: readonly ProgramFaq[];
  onAddFaq: () => void;
  onEditFaq: (id: string) => void;
  onRemoveFaq: (id: string) => void;
  onViewDefaultFaqs: () => void;

  isDefaultFaqsOpen: boolean;
  onDefaultFaqsOpenChange: (open: boolean) => void;
  /** EU-641 — itens do painel (catálogo + hidden em MEMÓRIA na criação). */
  defaultFaqItems: DefaultFaqsPanelItem[];
  defaultFaqsLoading?: boolean;
  defaultFaqsError?: boolean;
  onDefaultFaqsRetry?: () => void;
  onDefaultFaqToggle: (faqId: number, hidden: boolean) => void;

  isAddDialogOpen: boolean;
  onAddDialogOpenChange: (open: boolean) => void;
  dialogMode: FaqDialogMode;
  newFaqQuestion: string;
  newFaqAnswer: string;
  onNewFaqQuestionChange: (value: string) => void;
  onNewFaqAnswerChange: (value: string) => void;
  onSaveNewFaq: () => void;

  progress: number;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function ProgramFaqsPage({
  faqs,
  onAddFaq,
  onEditFaq,
  onRemoveFaq,
  onViewDefaultFaqs,
  isDefaultFaqsOpen,
  onDefaultFaqsOpenChange,
  defaultFaqItems,
  defaultFaqsLoading,
  defaultFaqsError,
  onDefaultFaqsRetry,
  onDefaultFaqToggle,
  isAddDialogOpen,
  onAddDialogOpenChange,
  dialogMode,
  newFaqQuestion,
  newFaqAnswer,
  onNewFaqQuestionChange,
  onNewFaqAnswerChange,
  onSaveNewFaq,
  progress,
  onBack,
  onSubmit,
  isSubmitting,
}: ProgramFaqsPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="programs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                <ArchiveIcon className="size-4" />
              </div>
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                Criação de programa
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Perguntas Frequentes
            </p>
          </div>

          <Progress value={progress} className="h-2 bg-secondary/40" />

          <form
            className="flex flex-1 flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            <div className="flex flex-col gap-5">
              <button
                type="button"
                onClick={onAddFaq}
                className="flex w-full flex-row items-center justify-between gap-2 rounded-2xl border border-border bg-card p-5 text-left shadow-sm transition-colors hover:border-primary"
              >
                <span className="text-base font-medium leading-none text-foreground">
                  Adicionar nova pergunta
                </span>
                <PlusIcon className="size-5 text-foreground" />
              </button>

              {faqs.map((faq) => (
                <FaqCard
                  key={faq.id}
                  faq={faq}
                  onEdit={() => onEditFaq(faq.id)}
                  onRemove={() => onRemoveFaq(faq.id)}
                />
              ))}
            </div>

            <div
              role="note"
              className="flex flex-row items-start gap-3 rounded-lg border border-border bg-card p-4"
            >
              <div className="flex shrink-0 pt-0.5 text-foreground">
                <TriangleAlertIcon className="size-4" />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                {/* EU-641 — copy do card (criação = sempre editável). */}
                <p className="text-sm font-medium leading-5 text-foreground">
                  {DEFAULT_FAQS_CARD_TITLE}
                </p>
                <p className="text-sm leading-5 text-muted-foreground">
                  {defaultFaqsCardCopy('program', false).body}{' '}
                  <button
                    type="button"
                    onClick={onViewDefaultFaqs}
                    className="font-medium text-primary hover:underline"
                  >
                    {defaultFaqsCardCopy('program', false).linkLabel}
                  </button>
                </p>
              </div>
            </div>

            <div className="mt-auto flex flex-row items-center justify-between pt-3">
              <Button type="button" variant="outline" size="lg" onClick={onBack}>
                Voltar
              </Button>
              <Button type="submit" size="lg" loading={isSubmitting}>
                Criar programa
              </Button>
            </div>
          </form>
        </div>
      </main>

      <AddFaqDialog
        open={isAddDialogOpen}
        onOpenChange={onAddDialogOpenChange}
        mode={dialogMode}
        question={newFaqQuestion}
        answer={newFaqAnswer}
        onQuestionChange={onNewFaqQuestionChange}
        onAnswerChange={onNewFaqAnswerChange}
        onSave={onSaveNewFaq}
      />

      {/* EU-641 — escolhas em memória até a criação (FQC-014/015). */}
      <DefaultFaqsPanel
        open={isDefaultFaqsOpen}
        onOpenChange={onDefaultFaqsOpenChange}
        items={defaultFaqItems}
        loading={defaultFaqsLoading}
        error={defaultFaqsError}
        onRetry={onDefaultFaqsRetry}
        onToggle={onDefaultFaqToggle}
      />
    </div>
  );
}

interface FaqCardProps {
  faq: ProgramFaq;
  onEdit: () => void;
  onRemove: () => void;
}

function FaqCard({ faq, onEdit, onRemove }: FaqCardProps) {
  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-row items-center gap-2">
        <span className="flex-1 text-base font-medium leading-none text-foreground">
          {faq.question}
        </span>
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Editar pergunta "${faq.question}"`}
          className="text-foreground transition-colors hover:text-primary"
        >
          <SquarePenIcon className="size-5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remover pergunta "${faq.question}"`}
          className="text-foreground transition-colors hover:text-destructive"
        >
          <Trash2Icon className="size-5" />
        </button>
      </div>
      <RichTextContent html={faq.answer} />
    </div>
  );
}
